import {
  computed,
  ref,
  Ref,
  toRaw,
} from 'vue';
import { Form } from 'ant-design-vue';

import { useFormHelpers } from '@/composables/useFormHelpers';
import {
  EBankModelFields,
  ETenantTaxType,
  MAX_BANKS_OPTIONS_LENGTH,
} from '@/contexts/accountingContext/domain/constants';
import { TTenantFormData } from '@/contexts/accountingContext/domain/types';
import tt from '@/i18n/utils/translateText';
import { TContractor, TContractorEditablePayload } from '@/contexts/contractorsContext/domain/types';
import { TBankAccount, TBankAccounts } from '@/contexts/accountingContext/components/BankSelector/types';
import { TSelectOption } from '@/ui/types';
import useViewState from '@/composables/useViewState';
import { useTenantsStore } from '@/stores/tenants';
import { showErrorNotification } from '@/domains/showErrorNotification';
import { useAppStore } from '@/stores/app';
import { TDomainError } from '@/types';
import { logger } from '@/utils';
import { fetchContractorsSuggestions } from '@/contexts/resourcesControlContext/services';
import { TReachedResponse } from '@/api/types';
import { fetchBankAccountsAutocomplete } from '@/contexts/accountingContext/services';

type TUseInteractWithForm = {
  isVatRatesV2Available: Ref<boolean>,
  formDisabled: Ref<boolean>,
  emit: (event: 'success' | 'close', ...args: any[]) => void,
};

export const useInteractWithForm = ({
  isVatRatesV2Available,
  formDisabled,
  emit,
}: TUseInteractWithForm) => {
  const { isLoading, setViewStateAs } = useViewState();
  const tenantsStore = useTenantsStore();
  const appStore = useAppStore();

  const modelRef = ref<TTenantFormData>({
    name: '',
    inn: null,
    kpp: null,
    taxType: ETenantTaxType.osno,
    bankName: null,
    bankBik: null,
    bankAddress: null,
    bankInn: null,
    bankKpp: null,
    bankCorrespondentAccount: null,
    bankCheckingAccount: null,
    phone: null,
    legalAddress: null,
    vatRateSlug: null,
  });

  const rulesRef = computed(() => ({
    name: [{
      required: true,
      message: tt('tenant.shared.form.rule.nameRequired'),
    }],
    taxType: [{
      required: true,
      message: tt('tenant.shared.form.rule.taxTypeRequired'),
    }],
    vatRateSlug: [{
      required: isVatRatesV2Available.value,
      message: tt('shared.rule.required'),
    }],
    legalAddress: [{
      required: true,
      message: tt('tenant.shared.form.rule.legalAddressRequired'),
    }],
    bankBik: [],
    bankCheckingAccount: [],
    kpp: [],
    inn: [{
      required: true,
      message: tt('shared.rule.innRequired'),
    }],
  }));

  const {
    validateInfos,

    validate,
    onControlChange,
    isFieldInvalid,
    setResponseErrors,
  } = useFormHelpers(modelRef, rulesRef);

  const availableVatRatesOptions = computed(() => appStore.getAvailableVatRatesOptions);

  const sberParams = computed(() => appStore.sberParams);
  const isOptionsLoading = ref(false);
  const isOptionsBankLoading = ref(false);
  const options = ref<TContractor[]>([]);
  const bankOptions = ref<TBankAccounts>([]);
  const formRef = ref<typeof Form | null>(null);
  const showBankAccount = ref(false);

  const submitDisabled = computed(() => formDisabled.value || isLoading.value);

  const handleFormSubmit = (payload: TTenantFormData) => {
    setViewStateAs.loading();
    tenantsStore.registerNewTenant(payload)
      .then(() => {
        setViewStateAs.done();
        emit('success');
        appStore.setSberParams(null);
      })
      .catch((error: TDomainError) => {
        setViewStateAs.withError();
        setResponseErrors(error);
        showErrorNotification(error);
      });
  };

  const onSubmit = () => {
    if (!formRef.value) return;
    validate()
      .then(() => {
        handleFormSubmit(toRaw(modelRef.value));
      })
      .catch((err) => {
        logger.log('error', err);
      });
  };

  const onClose = () => {
    if (sberParams.value && sberParams.value.userData) {
      appStore.setSberParams(null);
    }
    emit('close');
  };

  const handleTenantChange = (optionId: string, option: TContractor) => {
    const contractor = options.value.find((item: TContractor) => item?.id === optionId);
    if (contractor) {
      modelRef.value.name = option.name;
      modelRef.value.inn = option.inn;
      modelRef.value.kpp = option.kpp;
      modelRef.value.phone = option.phone;
      modelRef.value.legalAddress = option.legalAddress;
    }
  };

  const handleInputChange = (name: TContractorEditablePayload, value: string) => {
    modelRef.value[name] = value;

    // ИНН и КПП могут быть взаимозависимыми, поэтому есть необходимость обновлять данные по их валидности вместе
    if (name === 'inn' || name === 'kpp') {
      onControlChange({ name: 'inn' });
      onControlChange({ name: 'kpp' });
    } else {
      onControlChange({ name });
    }
  };

  const handleSearch = (query: string) => {
    options.value = [];
    if (!query) return;
    isOptionsLoading.value = true;
    modelRef.value.name = query;
    fetchContractorsSuggestions(query)
      .then((result: TReachedResponse<TContractor[]>) => {
        if (!result.data) return;
        const { data: contragents } = result;
        const serializedContragents = contragents.map((contragent: TContractor) => ({
          ...contragent,
          id: `${contragent.inn}-${contragent.kpp}`,
        }));
        options.value = serializedContragents;
      })
      .catch((err: TDomainError) => {
        logger.log('error', err);
      })
      .finally(() => {
        isOptionsLoading.value = false;
      });
  };

  const handleBankChange = (option: TBankAccount) => {
    modelRef.value.bankBik = option.bik;
    modelRef.value.bankName = option.name;
    modelRef.value.bankAddress = option.address;
    modelRef.value.bankInn = option.inn;
    modelRef.value.bankKpp = option.kpp;
    modelRef.value.bankCorrespondentAccount = option.correspondentAccount;

    showBankAccount.value = true;
  };

  const resetBankModelFields = () => {
    Object.keys(EBankModelFields).forEach((field: string) => {
      modelRef[field] = null;
    });

    showBankAccount.value = false;
  };

  const handleSearchBankChange = (query: string) => {
    onControlChange({ name: 'bankBik' });
    bankOptions.value = [];
    if (!query) {
      // Когда инпут пустой, нужно удалить ранее выбранный банк
      resetBankModelFields();
      return;
    }
    isOptionsBankLoading.value = true;
    fetchBankAccountsAutocomplete(query)
      .then((result: TReachedResponse<TBankAccounts>) => {
        if (result.data.length) {
          const { data: banks } = result;
          const serializedBanks = banks.map((bank: TBankAccount) => ({
            ...bank,
            id: `${bank.bik}-${bank.name}`,
          }));
          bankOptions.value = serializedBanks.slice(0, MAX_BANKS_OPTIONS_LENGTH);
        }
      })
      .catch((err: TDomainError) => {
        logger.log('error', err);
      })
      .finally(() => {
        isOptionsBankLoading.value = false;
      });
  };

  const bankInfo = computed(() => ({
    bankName: modelRef.value.bankName,
    bankCorrespondentAccount: modelRef.value.bankCorrespondentAccount,
    bankAddress: modelRef.value.bankAddress,
    bankInn: modelRef.value.bankInn,
    bankKpp: modelRef.value.bankKpp,
  }));

  const setFormData = () => {
    if (sberParams.value && sberParams.value.userData) {
      modelRef.value.name = sberParams.value.userData.organization.name;
      modelRef.value.inn = sberParams.value.userData.organization.inn;
      modelRef.value.kpp = sberParams.value.userData.organization.kpp;
      modelRef.value.bankBik = sberParams.value.userData.organization.bic;
      modelRef.value.phone = sberParams.value.userData.organization.phone || null;
      modelRef.value.legalAddress = sberParams.value.userData.organization.legalAddress || null;
    }
  };

  const handleVatRateChange = (vatRateOption: TSelectOption) => {
    modelRef.value.vatRateSlug = vatRateOption.value as string;

    onControlChange({ name: 'vatRateSlug' });
  };

  const taxType = computed(() => [{
    label: tt('tenant.shared.form.usn'),
    value: ETenantTaxType.usn,
  },
  {
    label: tt('tenant.shared.form.osno'),
    value: ETenantTaxType.osno,
  }]);

  return {
    validateInfos,
    rulesRef,
    modelRef,
    isOptionsLoading,
    isOptionsBankLoading,
    options,
    bankOptions,
    formRef,
    showBankAccount,
    submitDisabled,
    bankInfo,
    isLoading,
    taxType,
    availableVatRatesOptions,

    validate,
    onControlChange,
    isFieldInvalid,
    setResponseErrors,
    setFormData,
    handleFormSubmit,
    handleBankChange,
    handleTenantChange,
    handleInputChange,
    handleSearchBankChange,
    handleVatRateChange,
    handleSearch,
    onSubmit,
    onClose,
  };
};
